import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { store } from './app/store';
import { queryClient } from './app/queryClient';
import { bindLanguageToQueryCache } from './app/queryLanguage';
import { ToastNotification, showToastNotification } from '@/shared/ui/ToastNotification';
import { setUnauthorizedHandler } from '@/api/client';
import { authCookies } from '@/api/cookies';
import { setGuest } from '@/features/auth';
import { i18n, initI18n } from '@/shared/i18n';
import favicon from '@favicon-current';
// Theme-токены (CSS-переменные) грузятся до tailwind/index.css, чтобы Tailwind utility-классы
// типа bg-surface-page могли резолвить var(--surface-page). Алиас @theme-current
// настроен в vite.config.ts по VITE_THEME.
import '@theme-current';
import './index.css';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

// Язык кабинета. Инициализируем ДО рендера и синхронно: словари обоих языков уже
// в бандле, поэтому ждать нечего, а первый кадр обязан быть на нужном языке.
// initI18n сам определит выбор (localStorage['sp_lang'] — общий с лендингом,
// затем legacy-cookie, затем язык браузера) и запишет его обратно, чтобы
// `lang-id` в запросах к API совпадал с тем, что видит партнёр.
initI18n();

// Смена языка обесценивает кеш ответов: часть строк переводит сервер по `lang-id`.
bindLanguageToQueryCache(queryClient);

// Vite BASE_URL включает префикс инстанса: '/partners/private/' для private,
// '/partners/serious/' для serious и т.д. Срезаем trailing slash для basename
// react-router (он требует без него). Без instance в basename все deep-links
// (открытие /partners/private/postbacks напрямую, refresh внутри страницы)
// летят в catch-all 404 — pathname после basename='/partners' выглядит как
// '/private/postbacks', а такого роута нет.
const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

// Build-time бренд-тайтл (раньше ставил BrandProvider, который удалён).
document.title = import.meta.env.VITE_BRAND_TITLE || 'Wap';

// Build-time favicon по бренду (у фронтендера ставился через react-helmet
// <link rel="icon">; Helmet выпилен — инжектим в <head> напрямую, как title
// выше). index.html <link rel="icon"> не держит — Vite не префиксит абсолютный
// путь base-ом инстанса, поэтому импортируем .ico как hashed-ассет.
const faviconLink =
  document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
  document.createElement('link');
faviconLink.rel = 'icon';
faviconLink.href = favicon;
if (!faviconLink.isConnected) {
  document.head.appendChild(faviconLink);
}

// 401-handler для apiClient: любой 401 от API → принудительный разлогин (чистим
// cookies + react-query кеш, RTK auth-slice в guest → RequireAuth редиректит на
// /login + тост). Дедуп по статусу: если уже guest (пачка параллельных 401) —
// выходим, иначе наплодим clear/тосты. Авто-сброс — следующий логин вернёт
// статусу authenticated.
setUnauthorizedHandler(() => {
  if (store.getState().auth.status === 'guest') return;
  authCookies.clear();
  // Чистим и react-query кеш: иначе закешированный /me (gcTime ~5 мин) переживает
  // setGuest, и useMe внутри RequireAuth передиспатчивает setAuthenticated обратно
  // из кеша — статус прыгает в authenticated, редирект на /login не срабатывает.
  queryClient.clear();
  store.dispatch(setGuest());
  // Объясняем вынужденный разлогин: контейнер <ToastNotification /> вне Routes,
  // поэтому тост переживает редирект на /login. Только для принудительного
  // разлогина — ручной useLogout идёт мимо этого handler'а.
  showToastNotification(i18n.t('session.expired'), 'warning');
});

createRoot(rootElement).render(
  <StrictMode>
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={basename}>
          <App />
          <ToastNotification />
        </BrowserRouter>
      </QueryClientProvider>
    </ReduxProvider>
  </StrictMode>,
);
